// Sample events calendar build, explained and detailed over at
// https://justacoding.blog/react-calendar-component-example-with-events/

import Radio from '@mui/material/Radio'
import { styled } from '@mui/material/styles'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import ButtonMobileComponent from 'components/button-mobile'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import { useEffect, useState } from 'react'
import { CarpoolRequestOptionProps } from 'types/CalendarTypes'
import { getTimeNumber, getTimeStringFromNumber } from 'utils/constants'
import './index.css'
import { DateTimeProps } from 'types/CommunityTypes'
dayjs.extend(customParseFormat)

enum CarpoolRequestUpdate {
  SET_CARPOOL_TIME = 'Set Carpool Time',
  NOT_CARPOOLING = 'Not carpooling',
}

const CustomRadio = styled(Radio)(({ theme }) => ({
  marginLeft: '4px',
  color: '#A3A3A3', // Default color when not checked
  width: '16px',
  '&.Mui-checked': {
    color: '#E5C265', // Color when checked
  },
}))

// New reusable component for calendar action controls
const CalendarRequestUpdateComponent = ({
  selectedDates,
  carpoolRequestOptions,
  onSave,
}: {
  selectedDates: string[]
  carpoolRequestOptions: CarpoolRequestOptionProps
  onSave: (body: DateTimeProps) => void
  className?: string
}) => {
  // 1. All hooks at the top
  const { communityTime } = carpoolRequestOptions
  const [selectedOption, setSelectedOption] = useState<CarpoolRequestUpdate>()
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  // 2. Initialize state in useEffect
  useEffect(() => {
    setSelectedTime(communityTime >= 0 ? dayjs(getTimeStringFromNumber(communityTime), 'HH:mm') : null)
    setIsInitialLoading(false)
  }, [communityTime])

  // 3. Early return after hooks
  if (isInitialLoading) return null

  // 5. Handlers
  const handleSave = async () => {
    if (!selectedTime) return
    const time = selectedOption === CarpoolRequestUpdate.SET_CARPOOL_TIME ? getTimeNumber(selectedTime.toDate()) : -1
    const carpoolRequests: DateTimeProps = selectedDates.reduce<DateTimeProps>((acc, date) => {
      acc[date] = time
      return acc
    }, {})
    await onSave(carpoolRequests)
  }

  // 6. Render
  return (
    <div className={`flex flex-col`}>
      {/* radio buttons */}
      <div className='flex items-center gap-2 sm:gap-4'>
        {Object.entries(CarpoolRequestUpdate).map(([key, value]) => (
          <div key={key} className='flex items-center'>
            <CustomRadio size='small' checked={selectedOption === value} onChange={() => setSelectedOption(value)} />
            <div className={`${selectedOption === value ? 'text-headings' : 'text-gray-400'} ml-1 sm:ml-2`}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* This container is responsive.
          - `flex-col` (default): Stacks items on mobile.
          - `md:flex-row`: Switches to a horizontal row on medium screens and up.
          - `items-stretch`: Ensures children take full width in vertical mode.
          - `md:items-center`: Vertically aligns items in horizontal mode. */}
      <div className='w-full md:flex-1'>
        {/* This block renders the correct input based on the selected option */}
        {selectedOption === CarpoolRequestUpdate.SET_CARPOOL_TIME && (
          <div className='w-full flex flex-col md:flex-row items-stretch md:items-center gap-3'>
            {selectedTime && (
              <div className='w-full md:w-auto'>
                <TimePicker
                  value={dayjs(selectedTime)}
                  onChange={(d: Dayjs | null) => setSelectedTime(d)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#e5e5e5',
                            borderRadius: '8px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#e5e5e5',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#e5e5e5',
                            borderWidth: '1px',
                          },
                        },
                        width: '100%',
                        minWidth: '150px',
                      },
                      inputProps: {
                        readOnly: true,
                        style: { fontSize: '1rem' },
                      },
                    },
                  }}
                />
              </div>
            )}

            {/* The Save button is now INSIDE this flex container */}
            <div className='w-full md:w-auto'>
              <ButtonMobileComponent text='Save' className='w-full md:px-8' onClick={handleSave} />
            </div>
          </div>
        )}
        {selectedOption === CarpoolRequestUpdate.NOT_CARPOOLING && (
          // This flex container handles the responsive layout for the UNSET state
          <div className='w-full flex flex-col md:flex-row items-stretch md:items-center gap-3'>
            {/* The message now takes up the available space on desktop */}
            <div className='w-full flex items-center h-10 px-3 border border-gray-200 rounded-lg text-xs md:text-sm text-gray-600 bg-gray-50'>
              {`Carpool request will be removed`}
            </div>

            {/* The Save button is now INSIDE this flex container */}
            <div className='w-full md:w-auto'>
              <ButtonMobileComponent text='Save' className='w-full md:px-8' onClick={handleSave} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CalendarRequestUpdateComponent
